import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { fmt$ } from "./utils";

const DEPARTMENTS = [
  "Engineering", "Finance", "Operations", "Sales", "HR",
  "Marketing", "Supply Chain", "HSE", "Maintenance", "IT", "Executive",
];

const FILTERS = ["Active", "Ended", "All"];

export default function Directory({ employees, onChange }) {
  const [filter, setFilter] = useState("Active");
  const [allEmployees, setAllEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_no: "", user_id: "", department: "Engineering",
    position: "", salary: "", level: "Employee", line_manager_id: "",
  });
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [levels, setLevels] = useState(["Employee"]);
  const [managers, setManagers] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load the full roster (active + ended) once for the filter, plus the
  // support lists (invited users, hierarchy levels, managers).
  useEffect(() => {
    Promise.all([
      api.get("/tenant/users").then((r) => r.data).catch(() => []),
      api.get("/hierarchy/levels").then((r) => r.data).catch(() => ({ levels: ["Employee"] })),
      api.get("/hierarchy/employees").then((r) => r.data).catch(() => []),
      api.get("/hr/employees/all").then((r) => r.data).catch(() => []),
    ]).then(([users, hier, emps, allEmps]) => {
      // Filter out users that already link to an ACTIVE employee. Users
      // linked to ended-only records stay invite-eligible — the API will
      // walk them through the reactivate flow.
      const activeLinked = new Set(
        allEmps.filter((e) => !e.end_date && !e.ended_at).map((e) => e.user_id).filter(Boolean)
      );
      setInvitedUsers(users.filter((u) => !activeLinked.has(u.id) && u.active !== false));
      setLevels(hier.levels || ["Employee"]);
      setManagers((emps || []).filter((m) => !m.end_date));
      setAllEmployees(allEmps || []);
    });
  }, [employees]);

  const rows = useMemo(() => {
    if (filter === "Active") return allEmployees.filter((e) => !e.end_date && !e.ended_at);
    if (filter === "Ended") return allEmployees.filter((e) => e.end_date || e.ended_at);
    return allEmployees;
  }, [filter, allEmployees]);

  const activeCount = allEmployees.filter((e) => !e.end_date && !e.ended_at).length;
  const endedCount = allEmployees.length - activeCount;

  const create = async (e) => {
    e.preventDefault();
    const chosen = invitedUsers.find((u) => u.id === form.user_id);
    if (!chosen) {
      toast.error("Please pick an invited user first (Organization → Team to invite)");
      return;
    }
    const payload = {
      employee_no: form.employee_no,
      name: chosen.name || chosen.email,
      email: chosen.email,
      user_id: form.user_id,
      department: form.department,
      position: form.position,
      salary: Number(form.salary),
      level: form.level,
      line_manager_id: form.line_manager_id || null,
    };
    try {
      await api.post("/hr/employees", payload);
      toast.success("Employee added and linked to user account");
      resetForm();
      onChange();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409 · Backend detected an end-dated employee for this user_id.
      // Offer the admin an inline reactivate — preserves the historical
      // record instead of creating a duplicate row.
      if (err.response?.status === 409 && detail?.code === "employee_ended") {
        const doReactivate = window.confirm(
          `${detail.message}\n\nClick OK to reactivate ${detail.ended_employee_no} · ${detail.ended_employee_name} ` +
          `with the details you just entered.\n\nClick Cancel to keep the ended record untouched.`
        );
        if (doReactivate) {
          try {
            await api.patch(`/hr/employees/${detail.ended_employee_id}/reactivate`, {
              department: form.department,
              position: form.position,
              salary: Number(form.salary),
              level: form.level,
              line_manager_id: form.line_manager_id || null,
            });
            toast.success(`Reactivated ${detail.ended_employee_no} · ${detail.ended_employee_name}`);
            resetForm();
            onChange();
          } catch (reErr) {
            toast.error(formatApiError(reErr.response?.data?.detail) || "Reactivate failed");
          }
        }
        return;
      }
      toast.error(formatApiError(detail));
    }
  };

  const resetForm = () => setForm({
    employee_no: "", user_id: "", department: "Engineering", position: "",
    salary: "", level: "Employee", line_manager_id: "",
  });

  // No hard delete — API sets end_date. Confirm before ending, then show a
  // 30-second UNDO toast so a mis-click can be reversed with one tap.
  const endDate = async (id, name) => {
    if (!window.confirm(`End-date ${name}? The record stays in the audit trail; no data is removed. You can reactivate it later from the "Ended" tab.`)) return;
    try {
      await api.delete(`/hr/employees/${id}`);
      onChange();
      toast.success(`${name} end-dated`, {
        duration: 30000,
        description: "Click UNDO within 30 seconds to reactivate.",
        action: {
          label: "UNDO",
          onClick: async () => {
            try {
              await api.patch(`/hr/employees/${id}/reactivate`, {});
              toast.success(`${name} reactivated`);
              onChange();
            } catch (undoErr) {
              toast.error(formatApiError(undoErr.response?.data?.detail) || "Undo failed");
            }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reactivate = async (emp) => {
    if (!window.confirm(`Reactivate ${emp.employee_no} · ${emp.name}?\n\nStatus flips to 'active' and the leave balance resumes from today. You can update the department/position/salary right after.`)) return;
    try {
      await api.patch(`/hr/employees/${emp.id}/reactivate`, {});
      toast.success("Employee reactivated");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Per-employee custom leave entitlement (annual / sick day override).
  const editEntitlements = async (emp) => {
    const currAnn = emp.annual_entitlement ?? "";
    const currSick = emp.sick_entitlement ?? "";
    const ann = window.prompt(`Annual leave days for ${emp.name}\n(blank = inherit from level / tenant policy)`, currAnn);
    if (ann === null) return;
    const sick = window.prompt(`Sick leave days for ${emp.name}\n(blank = inherit from level / tenant policy)`, currSick);
    if (sick === null) return;
    const payload = {
      annual_entitlement: ann === "" ? null : Number(ann),
      sick_entitlement: sick === "" ? null : Number(sick),
    };
    try {
      await api.patch(`/hr/employees/${emp.id}/entitlements`, payload);
      toast.success("Entitlement override saved");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <form onSubmit={create} className="p-4 border-b border-zinc-950 bg-zinc-50" data-testid="employee-form">
        <div className="grid gap-3 md:grid-cols-3 mb-3">
          <input value={form.employee_no} onChange={(e) => set("employee_no", e.target.value)} required placeholder="Employee number" data-testid="employee-no-input" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none tnum focus:border-zinc-950" />
          <select value={form.user_id} onChange={(e) => set("user_id", e.target.value)} required data-testid="employee-user-select" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-950">
            <option value="">— Pick invited user —</option>
            {invitedUsers.map((u) => <option key={u.id} value={u.id}>{(u.name || u.email) + " · " + u.email}</option>)}
          </select>
          <select value={form.department} onChange={(e) => set("department", e.target.value)} data-testid="employee-dept-select" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-950">
            {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="grid gap-3 md:grid-cols-4 mb-3">
          <input value={form.position} onChange={(e) => set("position", e.target.value)} required placeholder="Position" data-testid="employee-position-input" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-950" />
          <input type="number" step="0.01" value={form.salary} onChange={(e) => set("salary", e.target.value)} required placeholder="Salary" data-testid="employee-salary-input" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none tnum focus:border-zinc-950" />
          <select value={form.level} onChange={(e) => set("level", e.target.value)} data-testid="employee-level-select" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-950">
            {levels.map((L) => <option key={L}>{L}</option>)}
          </select>
          <select value={form.line_manager_id} onChange={(e) => set("line_manager_id", e.target.value)} data-testid="employee-line-manager-select" className="bg-white border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-zinc-950">
            <option value="">— No line manager —</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{`${m.name} · ${m.level}`}</option>)}
          </select>
        </div>
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] flex items-center gap-1" data-testid="employee-create-btn">
          <Plus size={14} /> ADD EMPLOYEE
        </button>
      </form>
      {invitedUsers.length === 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-800" data-testid="employee-no-users-hint">
          No un-linked invited users found. Invite one on <b>Organization → Team</b> first — employees must map to an existing account.
        </div>
      )}
      <div className="border-b border-zinc-950 px-4 py-2 flex items-center gap-3 bg-white">
        <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">Filter</span>
        <div className="flex border border-zinc-950" data-testid="directory-filter">
          {FILTERS.map((f) => {
            const count = f === "Active" ? activeCount : f === "Ended" ? endedCount : allEmployees.length;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                data-testid={`directory-filter-${f.toLowerCase()}`}
                className={`px-3 py-1 text-[10px] font-mono tracking-[0.15em] uppercase border-l first:border-l-0 border-zinc-950 ${
                  filter === f ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"
                }`}
              >
                {f} · {count}
              </button>
            );
          })}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">#</th>
            <th className="text-left px-4 py-2.5">Name</th>
            <th className="text-left px-4 py-2.5">Department</th>
            <th className="text-left px-4 py-2.5">Position · Level</th>
            <th className="text-right px-4 py-2.5">Salary</th>
            <th className="text-left px-4 py-2.5">Days A/S</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-16" />
          </tr>
        </thead>
        <tbody data-testid="employees-tbody">
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-10 text-xs font-mono text-zinc-400">
                NO {filter.toUpperCase()} EMPLOYEES
              </td>
            </tr>
          )}
          {rows.map((e) => {
            const isEnded = !!(e.end_date || e.ended_at);
            return (
              <tr key={e.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isEnded ? "opacity-70" : ""}`}>
                <td className="px-4 py-2.5 tnum text-xs">{e.employee_no}</td>
                <td className="px-4 py-2.5">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-[10px] text-zinc-400">{e.email}</div>
                </td>
                <td className="px-4 py-2.5 text-zinc-600">{e.department}</td>
                <td className="px-4 py-2.5 text-zinc-600">
                  {e.position}
                  {e.level && <span className="ml-1 text-[10px] font-mono text-zinc-400">· {e.level}</span>}
                </td>
                <td className="px-4 py-2.5 text-right tnum">{fmt$(e.salary)}</td>
                <td className="px-4 py-2.5">
                  <button
                    onClick={() => editEntitlements(e)}
                    disabled={isEnded}
                    className="text-[10px] font-mono text-zinc-600 hover:text-[#FF3E00] border border-zinc-200 hover:border-[#FF3E00] px-2 py-0.5 disabled:opacity-40 disabled:hover:text-zinc-600 disabled:hover:border-zinc-200"
                    data-testid={`employee-entitlement-${e.id}`}
                    title={isEnded ? "Reactivate this employee to edit entitlements" : "Override annual / sick days for this employee"}
                  >
                    {(e.annual_entitlement ?? "—")}A / {(e.sick_entitlement ?? "—")}S
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  {isEnded ? (
                    <span
                      className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300"
                      title={e.end_date ? `Ended on ${e.end_date}` : "Ended"}
                    >
                      ENDED{e.end_date ? ` · ${e.end_date}` : ""}
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700">{(e.status || "active").toUpperCase()}</span>
                  )}
                </td>
                <td className="px-2">
                  {isEnded ? (
                    <button
                      onClick={() => reactivate(e)}
                      data-testid={`employee-reactivate-${e.id}`}
                      className="text-emerald-600 hover:text-emerald-800"
                      title="Reactivate this employee (clears the end date, resumes leave accrual)"
                    >
                      <ArrowClockwise size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => endDate(e.id, e.name)}
                      data-testid={`employee-end-date-${e.id}`}
                      className="text-zinc-400 hover:text-[#D0021B]"
                      title="End-date employee (no hard delete)"
                    >
                      <XCircle size={14} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
